import { useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { get } from "lodash"
import { TableRow as MUITableRow, TableCell } from "@mui/material"
import { Link, ArrowRight } from "@mui/icons-material"
import DisplayPositionName from "components/display-position-name"
import { treatAsFactor, treatAsBasket, isUnmodeled, formatLabel } from "helpers/securities"
import { numberFormatter } from "helpers/formatters/numbers"
import classNames from "classnames"
import { computeContributorFormatter } from "helpers/contributors"

function TableRow({
  selected,
  contributor,
  openSecurityProfile,
  openFactorProfile,
  openCompositeSecurity,
  openBasket,
  noPercent,
  hideSelectedField,
  enableBasketDrillDown,
  formatter,
}) {
  const asDrillDown = useMemo(() => treatAsBasket(contributor.assetClass) && enableBasketDrillDown, [contributor, enableBasketDrillDown])

  const showSecurity = useCallback(() => {
    const { id, exchange, ticker, assetClass } = contributor
    if (treatAsFactor(assetClass)) {
      openFactorProfile(ticker)
    } else if (assetClass === "index") {
      openCompositeSecurity({ type: assetClass, id })
    } else if (asDrillDown) {
      openBasket(contributor)
    } else {
      openSecurityProfile(exchange, ticker)
    }
  }, [contributor, asDrillDown, openFactorProfile, openSecurityProfile, openCompositeSecurity, openBasket])

  const description = useMemo(() => {
    const { id, ticker, country, notHeld } = contributor
    if (isUnmodeled(contributor)) return <div className={classNames({ "display-name-unmodeled": !notHeld })}>{formatLabel(contributor)}</div>
    return (
      <a onClick={showSecurity} className="factor-link">
        <DisplayPositionName {...{ id, ticker, country }} />
        {asDrillDown ? <ArrowRight /> : <Link className="icon" />}
      </a>
    )
  }, [contributor, asDrillDown, showSecurity])

  const valueCell = useMemo(() => {
    if (hideSelectedField) return null
    const value = get(contributor, selected.field)
    return (
      <TableCell align="right">
        {computeContributorFormatter({ formatter, noPercent })(value)}
      </TableCell>
    )
  }, [hideSelectedField, contributor, selected, noPercent, formatter])

  const titleValue = useMemo(() => {
    if (!contributor.assetClass && contributor.notHeld) return "Not held"

    return contributor.assetClass ? contributor.description : "Unmodeled"
  }, [contributor])

  return (
    <MUITableRow className={classNames("detail-row", { "not-held": contributor.notHeld })}>
      <TableCell className="name" title={titleValue}>
        {description}
      </TableCell>
      <TableCell align="right">{numberFormatter(contributor.size * 100)}</TableCell>
      {valueCell}
    </MUITableRow>
  )
}

TableRow.propTypes = {
  selected: PropTypes.object.isRequired,
  contributor: PropTypes.object.isRequired,
  openSecurityProfile: PropTypes.func.isRequired,
  openFactorProfile: PropTypes.func.isRequired,
  openCompositeSecurity: PropTypes.func,
  openBasket: PropTypes.func,
  noPercent: PropTypes.bool,
  hideSelectedField: PropTypes.bool,
  enableBasketDrillDown: PropTypes.bool,
  formatter: PropTypes.func,
}

export default TableRow
